import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatUAH } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Eye, Printer, FileSpreadsheet, FileText, Pencil, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const PERIODS = [
  { key: "all", label: "Весь час" },
  { key: "today", label: "Сьогодні" },
  { key: "yesterday", label: "Вчора" },
  { key: "week", label: "Поточний тиждень" },
  { key: "month", label: "Поточний місяць" },
  { key: "prev_month", label: "Попередній місяць" },
  { key: "range", label: "Довільний період…" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  const d = String(iso).slice(0, 10).split("-");
  return d.length === 3 ? `${d[2]}.${d[1]}.${d[0]}` : iso;
}

export default function Payments() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailEmpId, setDetailEmpId] = useState("");
  const [periodKey, setPeriodKey] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [bucketKey, setBucketKey] = useState("all");  // Iteration 25: category filter
  const [form, setForm] = useState({ employee_id: "", amount: 0, period: "", comment: "" });
  const [editPayout, setEditPayout] = useState(null);  // Iteration 23: admin-only edit dialog
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
  }, []);
  const isAdmin = me?.role === "admin";

  const load = async () => {
    try { const r = await api.get("/payments/summary"); setRows(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const buildQuery = () => {
    const p = new URLSearchParams();
    if (periodKey === "range") {
      if (fromDate) p.set("from_date", fromDate);
      if (toDate) p.set("to_date", toDate);
    } else if (periodKey !== "all") {
      p.set("period", periodKey);
    }
    if (bucketKey !== "all") p.set("bucket", bucketKey);
    const s = p.toString();
    return s ? `?${s}` : "";
  };

  const BUCKETS = [
    { key: "all", label: "Усі категорії" },
    { key: "clinic", label: "Клініка" },
    { key: "business_trip", label: "Відрядження" },
    { key: "external_doctor", label: "Зовнішні лікарі" },
  ];

  const startCreate = (row) => {
    setForm({ employee_id: row.employee_id, amount: row.debt > 0 ? row.debt : 0,
              period: new Date().toISOString().slice(0, 7), comment: "" });
    setOpen(true);
  };
  const save = async () => {
    try { await api.post("/payments", form); toast.success("Виплату збережено"); setOpen(false); await load(); if (detailEmpId) await reloadDetail(detailEmpId); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const reloadDetail = async (empId) => {
    try {
      const r = await api.get(`/payments/employee/${empId}${buildQuery()}`);
      setDetail(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openDetail = async (row) => {
    setDetailEmpId(row.employee_id);
    setDetailOpen(true);
    await reloadDetail(row.employee_id);
  };

  useEffect(() => {
    if (detailOpen && detailEmpId) reloadDetail(detailEmpId);
  }, [periodKey, fromDate, toDate, bucketKey, detailOpen, detailEmpId, reloadDetail]);

  const downloadExport = async (kind) => {
    if (!detailEmpId) return;
    try {
      const r = await api.get(`/payments/employee/${detailEmpId}/export.${kind}${buildQuery()}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `salary_${detail?.employee?.full_name || "employee"}_${periodKey}.${kind}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Не вдалось згенерувати " + kind.toUpperCase()); }
  };

  const printDetail = () => {
    if (!detail) return;
    const w = window.open("", "_blank");
    if (!w) return;
    const rowsHtml = detail.accruals.map((a, i) => `
      <tr>
        <td class="c">${i + 1}</td>
        <td>${fmtDate(a.operation_date)}</td>
        <td>${a.patient_name || "—"}</td>
        <td class="c">${a.patient_chart_no || ""}</td>
        <td>${a.operation_type || "—"}</td>
        <td class="c">${a.operation_eye || ""}</td>
        <td class="r">${(a.operation_total || 0).toLocaleString("uk-UA")}</td>
        <td class="c">${a.rule_type === "fixed" ? "Фікс." : "%"}</td>
        <td class="c">${a.rule_label}</td>
        <td class="r"><b>${(a.amount || 0).toLocaleString("uk-UA")}</b></td>
      </tr>`).join("");
    const bp = detail.balance_period;
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"/><title>Зарплата</title>
    <style>
      body{font-family:'DejaVu Sans','Segoe UI',Arial,sans-serif;margin:20px;color:#0F2027}
      h1{margin:0 0 6px}
      .sub{color:#334155;margin-bottom:14px;font-size:13px}
      .kpi{display:flex;gap:8px;margin:12px 0;flex-wrap:wrap}
      .kpi div{border:1px solid #CBD5E1;padding:6px 12px;border-radius:6px;font-size:12px}
      .kpi b{display:block;font-size:15px;margin-top:2px}
      table{width:100%;border-collapse:collapse;font-size:11px;margin-top:6px}
      th{background:#0F2027;color:#fff;padding:5px;text-align:left}
      td{border:1px solid #CBD5E1;padding:4px 5px;vertical-align:top}
      td.r{text-align:right}
      td.c{text-align:center}
      tr:nth-child(even) td{background:#F8FAFC}
      .totals td{background:#E2E8F0;font-weight:600}
      @media print{.no-print{display:none}}
      button{padding:6px 12px;margin-right:6px;cursor:pointer}
    </style></head><body>
      <div class="no-print" style="margin-bottom:12px">
        <button onclick="window.print()">🖨 Друкувати</button>
        <button onclick="window.close()">Закрити</button>
      </div>
      <h1>Зарплатна відомість — ${detail.employee?.full_name || ""}</h1>
      <div class="sub">Період: <b>${detail.period.label}</b> · Роль: <b>${detail.employee?.role_label || detail.employee?.role || ""}</b> · Категорія: <b>${detail.bucket?.label || "Усі категорії"}</b></div>
      <div class="kpi">
        <div>Операцій<b>${bp.operations_count}</b></div>
        <div>Сума операцій<b>${(bp.operations_total || 0).toLocaleString("uk-UA")} ₴</b></div>
        <div>Нараховано<b>${(bp.accrued || 0).toLocaleString("uk-UA")} ₴</b></div>
        <div>Виплачено<b style="color:#059669">${(bp.paid || 0).toLocaleString("uk-UA")} ₴</b></div>
        <div>Борг<b style="color:#DC2626">${(bp.debt || 0).toLocaleString("uk-UA")} ₴</b></div>
      </div>
      <table>
        <thead><tr>
          <th>#</th><th>Дата</th><th>Пацієнт</th><th>Карта №</th><th>Вид операції</th>
          <th>Око</th><th>Сума опер.</th><th>Тип</th><th>Ставка</th><th>Нараховано</th>
        </tr></thead>
        <tbody>${rowsHtml || `<tr><td colspan="10" style="text-align:center;color:#94a3b8;padding:18px">Немає нарахувань за обраний період</td></tr>`}
        <tr class="totals"><td colspan="6" class="r">РАЗОМ:</td>
        <td class="r">${(bp.operations_total || 0).toLocaleString("uk-UA")}</td>
        <td></td><td></td><td class="r">${(bp.accrued || 0).toLocaleString("uk-UA")}</td></tr>
        </tbody>
      </table>
    </body></html>`);
    w.document.close();
  };

  const total = rows.reduce(
    (acc, r) => ({ accrued: acc.accrued + r.accrued, paid: acc.paid + r.paid, debt: acc.debt + r.debt }),
    { accrued: 0, paid: 0, debt: 0 }
  );

  return (
    <div data-testid="payments-page" className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="heading text-3xl font-bold text-[#0F2027]">Виплати</h1>
          <p className="text-sm text-slate-500">Нарахування та виплати по працівниках</p>
        </div>
        {(isAdmin || me?.role === "director") && (
          <Button variant="outline" size="sm" data-testid="btn-recompute-all"
            onClick={async () => {
              if (!window.confirm("Перерахувати всі нарахування за поточними правилами? Дія оновить historical suми відповідно до налаштувань.")) return;
              try {
                const r = await api.post("/settings/salary-rules/recompute-all");
                toast.success(`Перераховано: ${r.data.lines_created} нарахувань на ${r.data.ops_touched} операціях`);
                await load();
                if (detailEmpId) await reloadDetail(detailEmpId);
              } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
            }}
          >
            <RefreshCw className="h-4 w-4 mr-1.5" />Перерахувати за поточними правилами
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="gv-card"><CardContent className="p-6"><div className="text-xs uppercase tracking-wider text-slate-500">Нараховано</div><div className="gv-kpi-value">{formatUAH(total.accrued)}</div></CardContent></Card>
        <Card className="gv-card"><CardContent className="p-6"><div className="text-xs uppercase tracking-wider text-slate-500">Виплачено</div><div className="gv-kpi-value text-emerald-700">{formatUAH(total.paid)}</div></CardContent></Card>
        <Card className="gv-card"><CardContent className="p-6"><div className="text-xs uppercase tracking-wider text-slate-500">Заборгованість</div><div className="gv-kpi-value text-red-600">{formatUAH(total.debt)}</div></CardContent></Card>
      </div>

      <Card className="gv-card">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600 sticky top-0 z-10">
              <tr>
                <th className="text-left p-4">ПІБ</th>
                <th className="text-left p-4">Роль</th>
                <th className="text-right p-4">Нараховано</th>
                <th className="text-right p-4">Виплачено</th>
                <th className="text-right p-4">Заборгованість</th>
                <th className="text-right p-4">Дії</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.employee_id} className="border-t border-slate-100 hover:bg-slate-50/50">
                  <td className="p-4 font-medium">{r.full_name}</td>
                  <td className="p-4"><Badge variant="secondary" className="bg-teal-50 text-teal-700 hover:bg-teal-50">{r.role_label}</Badge></td>
                  <td className="p-4 text-right tabular-nums">{formatUAH(r.accrued)}</td>
                  <td className="p-4 text-right tabular-nums text-emerald-700">{formatUAH(r.paid)}</td>
                  <td className="p-4 text-right tabular-nums font-semibold text-red-600">{formatUAH(r.debt)}</td>
                  <td className="p-4 text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="icon" variant="ghost" onClick={() => openDetail(r)} data-testid={`payment-detail-${r.employee_id}`}><Eye className="h-4 w-4" /></Button>
                      <Button size="sm" onClick={() => startCreate(r)} data-testid={`pay-employee-${r.employee_id}`} className="bg-[#0F2027]"><Plus className="h-4 w-4 mr-1" />Виплатити</Button>
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-400">Немає записів</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Нова виплата</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-4">
            <div><Label>Сума</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value || 0) })} /></div>
            <div><Label>Період (YYYY-MM)</Label><Input value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} /></div>
            <div><Label>Коментар</Label><Input value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Скасувати</Button>
            <Button onClick={save} data-testid="save-payment" className="bg-[#0F2027]">Зберегти</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span>Зарплата: {detail?.employee?.full_name}</span>
              {detail && <Badge variant="secondary" className="bg-teal-50 text-teal-700">{detail.employee?.role_label || detail.employee?.role}</Badge>}
            </DialogTitle>
          </DialogHeader>

          {detail && (
            <div className="space-y-4">
              {/* Period selector */}
              <div className="flex flex-wrap items-end gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="min-w-48">
                  <Label className="text-xs">Період</Label>
                  <Select value={periodKey} onValueChange={setPeriodKey}>
                    <SelectTrigger data-testid="salary-period-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PERIODS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {periodKey === "range" && (
                  <>
                    <div><Label className="text-xs">Від</Label>
                      <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="salary-from-date" /></div>
                    <div><Label className="text-xs">До</Label>
                      <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="salary-to-date" /></div>
                  </>
                )}
                <div className="text-xs text-slate-500 ml-auto">
                  <div>Обраний період:</div>
                  <div className="font-semibold text-[#0F2027]">{detail.period.label}</div>
                </div>
              </div>

              {/* KPI cards for the selected period */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="p-3 border border-slate-200 rounded-md bg-white">
                  <div className="text-slate-500 text-xs">Операцій</div>
                  <div className="font-bold text-lg tabular-nums">{detail.balance_period.operations_count}</div>
                </div>
                <div className="p-3 border border-slate-200 rounded-md bg-white">
                  <div className="text-slate-500 text-xs">Сума операцій</div>
                  <div className="font-bold text-lg tabular-nums">{formatUAH(detail.balance_period.operations_total)}</div>
                </div>
                <div className="p-3 border border-emerald-200 rounded-md bg-emerald-50/40">
                  <div className="text-slate-500 text-xs">Нараховано</div>
                  <div className="font-bold text-lg tabular-nums">{formatUAH(detail.balance_period.accrued)}</div>
                </div>
                <div className="p-3 border border-sky-200 rounded-md bg-sky-50/40">
                  <div className="text-slate-500 text-xs">Виплачено</div>
                  <div className="font-bold text-lg tabular-nums text-emerald-700">{formatUAH(detail.balance_period.paid)}</div>
                </div>
                <div className="p-3 border border-rose-200 rounded-md bg-rose-50/40">
                  <div className="text-slate-500 text-xs">Борг</div>
                  <div className="font-bold text-lg tabular-nums text-red-600">{formatUAH(detail.balance_period.debt)}</div>
                </div>
              </div>

              {/* Export buttons */}
              <div className="flex gap-2 flex-wrap items-end">
                <div>
                  <Label className="text-xs">Категорія для друку/експорту</Label>
                  <Select value={bucketKey} onValueChange={setBucketKey}>
                    <SelectTrigger className="min-w-56" data-testid="salary-bucket-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {BUCKETS.map((b) => <SelectItem key={b.key} value={b.key}>{b.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button size="sm" variant="outline" onClick={printDetail} data-testid="salary-print">
                  <Printer className="h-4 w-4 mr-1.5" />Друкувати
                </Button>
                <Button size="sm" variant="outline" onClick={() => downloadExport("xlsx")} data-testid="salary-xlsx">
                  <FileSpreadsheet className="h-4 w-4 mr-1.5" />Excel
                </Button>
                <Button size="sm" variant="outline" onClick={() => downloadExport("pdf")} data-testid="salary-pdf">
                  <FileText className="h-4 w-4 mr-1.5" />PDF
                </Button>
              </div>

              {/* Accruals detail — grouped by bucket (Iteration 24) */}
              {(() => {
                const styles = {
                  clinic: {
                    label: "Клініка",
                    headerBg: "bg-emerald-50 border-emerald-200",
                    headerText: "text-emerald-800",
                    amountText: "text-emerald-700",
                    amountBg: "bg-emerald-50/40",
                    thBg: "bg-emerald-100",
                  },
                  business_trip: {
                    label: "Відрядження",
                    headerBg: "bg-amber-50 border-amber-200",
                    headerText: "text-amber-800",
                    amountText: "text-amber-700",
                    amountBg: "bg-amber-50/40",
                    thBg: "bg-amber-100",
                  },
                  external_doctor: {
                    label: "Зовнішні лікарі",
                    headerBg: "bg-sky-50 border-sky-200",
                    headerText: "text-sky-800",
                    amountText: "text-sky-700",
                    amountBg: "bg-sky-50/40",
                    thBg: "bg-sky-100",
                  },
                };
                const rowsByBucket = { clinic: [], business_trip: [], external_doctor: [] };
                (detail.accruals || []).forEach((a) => {
                  const b = rowsByBucket[a.bucket] ? a.bucket : "clinic";
                  rowsByBucket[b].push(a);
                });

                const renderGroup = (key) => {
                  const rows = rowsByBucket[key];
                  if (!rows.length) return null;
                  const s = styles[key];
                  const subtotalAmt = rows.reduce((sum, r) => sum + (r.amount || 0), 0);
                  const subtotalOps = rows.reduce((sum, r) => sum + (r.operation_total || 0), 0);
                  return (
                    <div key={key} className="mb-4">
                      <div className={`px-3 py-2 ${s.headerBg} border rounded-t-md flex items-center justify-between`}>
                        <div className={`text-sm font-semibold ${s.headerText}`}>
                          {s.label} <span className="text-xs font-normal text-slate-500">({rows.length} нарах.)</span>
                        </div>
                        <div className="text-xs text-slate-600">
                          Сума операцій: <b className="tabular-nums">{formatUAH(subtotalOps)}</b>
                          <span className="mx-2 text-slate-300">|</span>
                          Нараховано: <b className={`tabular-nums ${s.amountText}`}>{formatUAH(subtotalAmt)}</b>
                        </div>
                      </div>
                      <div className="border border-slate-200 border-t-0 rounded-b-md overflow-hidden">
                        <div className="overflow-auto max-h-[300px]">
                          <table className="w-full text-xs">
                            <thead className="bg-slate-100 text-[11px] uppercase tracking-wider sticky top-0 text-slate-700">
                              <tr>
                                <th className="p-2 text-center">#</th>
                                <th className="p-2 text-left">Дата опер.</th>
                                <th className="p-2 text-left">Пацієнт</th>
                                <th className="p-2 text-center">Карта №</th>
                                <th className="p-2 text-left">Вид операції</th>
                                <th className="p-2 text-center">Око</th>
                                <th className="p-2 text-right">Сума опер.</th>
                                <th className="p-2 text-center">Тип</th>
                                <th className="p-2 text-center">Ставка</th>
                                <th className={`p-2 text-right ${s.thBg}`}>Нараховано</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white">
                              {rows.map((a, i) => (
                                <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50">
                                  <td className="p-2 text-center text-slate-500">{i + 1}</td>
                                  <td className="p-2 tabular-nums">{fmtDate(a.operation_date)}</td>
                                  <td className="p-2 font-medium">{a.patient_name || <span className="text-slate-400">—</span>}</td>
                                  <td className="p-2 text-center tabular-nums text-slate-500">{a.patient_chart_no || ""}</td>
                                  <td className="p-2">{a.operation_type || <span className="text-slate-400">—</span>}</td>
                                  <td className="p-2 text-center">
                                    {a.operation_eye ? (
                                      <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                        a.operation_eye === "OD" ? "bg-emerald-50 text-emerald-700" :
                                        a.operation_eye === "OS" ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-slate-600"
                                      }`}>{a.operation_eye}</span>
                                    ) : ""}
                                  </td>
                                  <td className="p-2 text-right tabular-nums">{formatUAH(a.operation_total)}</td>
                                  <td className="p-2 text-center">
                                    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                      a.rule_type === "fixed" ? "bg-amber-50 text-amber-700" : "bg-blue-50 text-blue-700"
                                    }`}>{a.rule_type === "fixed" ? "Фікс." : "%"}</span>
                                  </td>
                                  <td className="p-2 text-center text-slate-600">{a.rule_label}</td>
                                  <td className={`p-2 text-right tabular-nums font-bold ${s.amountText} ${s.amountBg}`}>{formatUAH(a.amount)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  );
                };

                const hasAny = Object.values(rowsByBucket).some((rows) => rows.length > 0);
                return (
                  <div>
                    <h4 className="heading font-semibold mb-2">
                      Нарахування ({detail.accruals.length})
                    </h4>
                    {!hasAny && <div className="text-slate-400 text-sm py-6 text-center border border-dashed border-slate-200 rounded-md">Немає нарахувань за обраний період</div>}
                    {renderGroup("clinic")}
                    {renderGroup("business_trip")}
                    {renderGroup("external_doctor")}
                    {hasAny && (
                      <div className="flex justify-end gap-6 p-3 bg-slate-100 border border-slate-200 rounded-md text-sm">
                        <span>Всього операцій: <b className="tabular-nums">{formatUAH(detail.balance_period.operations_total)}</b></span>
                        <span>Всього нараховано: <b className="tabular-nums text-emerald-700">{formatUAH(detail.balance_period.accrued)}</b></span>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Payouts */}
              <div>
                <h4 className="heading font-semibold mb-2">Виплати ({detail.payouts.length})</h4>
                <div className="space-y-1.5 max-h-40 overflow-auto">
                  {detail.payouts.map((p) => (
                    <div key={p.id} className="flex justify-between items-center border border-slate-200 rounded p-2 text-sm">
                      <span className="text-slate-600">{fmtDate(p.created_at)} · {p.period || "—"}{p.comment ? ` · ${p.comment}` : ""}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold tabular-nums">{formatUAH(p.amount)}</span>
                        {isAdmin && (
                          <>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-500 hover:text-[#0F2027]"
                                    onClick={() => setEditPayout({ ...p })}
                                    data-testid={`edit-payout-${p.id}`} title="Редагувати виплату">
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-500 hover:text-rose-600"
                                    onClick={async () => {
                                      if (!window.confirm(`Видалити виплату на ${formatUAH(p.amount)}?`)) return;
                                      try { await api.delete(`/payments/${p.id}`); toast.success("Виплату видалено"); await reloadDetail(detailEmpId); await load(); }
                                      catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                                    }}
                                    data-testid={`delete-payout-${p.id}`} title="Видалити виплату">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                  {detail.payouts.length === 0 && <div className="text-slate-400 text-sm">Немає виплат за обраний період</div>}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!editPayout} onOpenChange={(v) => !v && setEditPayout(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Редагувати виплату</DialogTitle>
          </DialogHeader>
          {editPayout && (
            <div className="grid grid-cols-1 gap-4">
              <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded p-2">
                ⚠️ Редагування виплат доступне лише адміністратору. Зміна фіксується в audit-log.
              </div>
              <div><Label>Сума</Label>
                <Input type="number" data-testid="edit-payout-amount"
                       value={editPayout.amount}
                       onChange={(e) => setEditPayout({ ...editPayout, amount: parseFloat(e.target.value || 0) })} />
              </div>
              <div><Label>Період (YYYY-MM)</Label>
                <Input value={editPayout.period || ""}
                       onChange={(e) => setEditPayout({ ...editPayout, period: e.target.value })} />
              </div>
              <div><Label>Коментар</Label>
                <Input value={editPayout.comment || ""}
                       onChange={(e) => setEditPayout({ ...editPayout, comment: e.target.value })} />
              </div>
              <div className="text-xs text-slate-500">
                Створено: {fmtDate(editPayout.created_at)} · id: <code className="text-[10px]">{editPayout.id?.slice(0, 8)}</code>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPayout(null)}>Скасувати</Button>
            <Button data-testid="save-edit-payout" className="bg-[#0F2027]"
              onClick={async () => {
                try {
                  await api.put(`/payments/${editPayout.id}`, {
                    amount: editPayout.amount,
                    period: editPayout.period || "",
                    comment: editPayout.comment || "",
                  });
                  toast.success("Виплату оновлено");
                  setEditPayout(null);
                  await reloadDetail(detailEmpId);
                  await load();
                } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
              }}
            >Зберегти зміни</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
